/* eslint-disable max-lines */
import {
  useCreateLocationAlarmIncident,
  useLocationById,
} from '@hakimo-ui/hakimo/data-access';
import {
  Situation,
  UnifiedMonitoringProduct,
  UpsertLocationAlarmIncidentDTO,
  UpsertLocationAlarmIncidentResponseDTO,
} from '@hakimo-ui/hakimo/types';
import {
  eventTracker,
  isExistingIncidentResponse,
  toast,
  useUser,
} from '@hakimo-ui/hakimo/util';
import { Alert, Button, Textarea } from '@hakimo-ui/shared/ui-base';
import { XCircleIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';
import { useAlarmGroupStatic } from '../../unified-alarm-group/alarm-provider/AlarmGroupProvider';

interface Props {
  locationId: string;
  locationAlarmId: string;
  navToEscalation: (
    comment?: string,
    situation?: string,
    subSituation?: string
  ) => void;
  onCancel: () => void;
  selectedSituation?: Situation;
  product?: UnifiedMonitoringProduct;
}

export function PreEscalate(props: Props) {
  const {
    locationId,
    locationAlarmId,
    navToEscalation,
    onCancel,
    selectedSituation,
    product,
  } = props;
  const isScanContext = product === UnifiedMonitoringProduct.SCAN;
  const [textValue, setTextValue] = useState(
    selectedSituation ? `${selectedSituation.label}:` : ''
  );
  const [showEmptyError, setShowEmptyError] = useState(false);

  const hasSubSituations =
    selectedSituation?.subSituations &&
    selectedSituation.subSituations.length > 0;

  const locationDetails = useLocationById(locationId).data;
  const onSuccess = (data: UpsertLocationAlarmIncidentResponseDTO) => {
    // A 200 does not necessarily mean an escalation was raised: v3 allows one
    // active incident per (location, source group, alarm type), so escalating a
    // second alarm in that scope returns the existing one untouched. Reporting
    // success there would be a lie, and walking on to the evidence step would
    // attach it to an escalation this operator did not open.
    if (isExistingIncidentResponse(data)) {
      toast(
        `This alarm is already covered by an open escalation at ${locationDetails?.name}. Resolve or update that escalation instead.`,
        { type: 'warning' }
      );
      return;
    }
    toast(`Alarm is being escalated at ${locationDetails?.name}`, {
      type: 'success',
    });
    navToEscalation(textValue, selectedSituation?.label);
  };
  const user = useUser();
  const { dataVersion, sourceGroupId } = useAlarmGroupStatic();
  // Escalation is a high-stakes action and the failure was silent: the button
  // simply stopped loading. v3 rejects a create without a source group (400),
  // which is reachable whenever the alarm group arrives without one, so an
  // operator has to be told rather than left assuming the alarm was escalated.
  const onError = (err: Error) => {
    toast(`Could not escalate: ${err.message}`, { type: 'error' });
  };
  const mutation = useCreateLocationAlarmIncident(
    onSuccess,
    onError,
    dataVersion
  );

  const onTextValueChange = (val: string) => {
    setTextValue(val);
    if (showEmptyError && val.trim() !== '') {
      setShowEmptyError(false);
    }
  };

  const createPayload = (comment: string) => {
    const payload: UpsertLocationAlarmIncidentDTO = {
      location_id: +locationId,
      location_alarm_id: locationAlarmId,
      tenant_id: locationDetails?.tenant_id,
      title: `Escalation alert: Ongoing incident at ${locationDetails?.name}`,
      created_by: user.id,
      description: comment,
      // v3 scopes an incident to the alarm's source group and rejects a create
      // without it (400). Undefined on v2, where the legacy controller ignores
      // the field and keeps location-level behaviour.
      source_group_id: sourceGroupId,
      // The alarm's product. The backend applies one dedup rule to all of
      // them -- one active incident per (source group, alarm type).
      product,
    };
    return payload;
  };

  const handleSubSituationClick = (subSituation: string) => {
    const comment = `${selectedSituation?.label} - ${subSituation}`;

    if (!isScanContext) {
      const payload: UpsertLocationAlarmIncidentDTO = createPayload(comment);
      mutation.mutate(payload);
    } else {
      navToEscalation(comment, selectedSituation?.label, subSituation);
    }
  };

  const onEscalate = () => {
    if (textValue.trim() === '') {
      setShowEmptyError(true);
      return;
    }
    // Only send the API request if not in scan context
    if (!isScanContext) {
      const payload: UpsertLocationAlarmIncidentDTO = createPayload(textValue);
      mutation.mutate(payload);
    } else {
      navToEscalation(textValue, selectedSituation?.label);
    }
  };

  return (
    <div className="divide-onlight-line-3 dark:divide-ondark-line-3 space-y-6 divide-y">
      <div className="pt-4 font-bold">
        This is being escalated. Please provide a brief update on the current
        situation.
      </div>

      {hasSubSituations ? (
        <div className="relative pt-6">
          <Button
            variant="icon"
            onClick={onCancel}
            className="absolute right-0 top-0"
          >
            <XCircleIcon className="h-5 w-5" />
          </Button>
          <div className="space-y-4">
            <div className="text-onlight-text-2 dark:text-ondark-text-2 font-sans text-sm font-semibold">
              {selectedSituation?.label}
            </div>
            <div className="flex flex-wrap items-start justify-start gap-2">
              {selectedSituation?.subSituations?.map((subSituation, i) => (
                <Button
                  key={i}
                  onClick={() => handleSubSituationClick(subSituation)}
                  disabled={mutation.isLoading}
                  className="p-1 px-2"
                >
                  {subSituation}
                </Button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="space-y-2 pt-6">
            {showEmptyError && (
              <Alert type="error" size="small">
                Please add a comment before proceeding.
              </Alert>
            )}
            <Textarea
              onChange={onTextValueChange}
              value={textValue}
              rows={4}
              placeholder="Even a 10 word insight would do the work."
            />
          </div>
          <div className="space-x-4 pt-6 text-center">
            <Button
              variant="primary"
              onClick={onEscalate}
              loading={mutation.isLoading}
              disabled={mutation.isLoading}
              onSideEffect={eventTracker('create_location_incident')}
            >
              Proceed
            </Button>
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default PreEscalate;
